/**
 * Groundwork Tasks v1 — task detail drawer (spec §Task anatomy).
 *
 * Full anatomy, comments with @mentions, attachments, per-type quick action,
 * and the state actions (claim / snooze / done / cancel / reopen). Done and
 * Cancel follow the Undo Doctrine: 10-second undo toast, nothing hard-deletes.
 *
 * Tasks talk back (#1645): the drawer shows the Sent → Confirmed line and a
 * plain-words timeline, and offers each side its own actions —
 *   the person doing it:  I'm on it · Hand it back (one line, required when
 *                         someone else asked) · Waiting on… · Pass back
 *   the person who asked: Confirm handled / Not handled · Bump to the top of
 *                         their list · Give it to someone else
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TaskPeople } from "@/components/task-people";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookingApprovalPanel } from "@/components/booking-approval-panel";
import {
    AlarmClock,
    AlertTriangle,
    ArrowRight,
    Ban,
    CalendarClock,
    Check,
    Download,
    Hand,
    Hourglass,
    Link2,
    Paperclip,
    Play,
    RotateCcw,
    Undo2,
    X,
} from "lucide-react";
import { api, downloadAuthedFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
    ageChip,
    bumpWords,
    firstName,
    quickActionTarget,
    renderMentionText,
    UNDO_TOAST_MS,
    waitedFor,
    type Task,
    type TaskDetailResponse,
    type TimelineEntry,
} from "@/lib/tasks";
import { TaskProgressRail, type RailTone } from "@/components/task-progress";
import { DoneLooksLikeResult } from "@/components/done-looks-like-result";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { orgTodayKey } from "@/lib/datetime";
import { DrawerNote } from "@/components/whats-new/drawer-note";
import { CantDoDialog, useCantDoDecide } from "@/components/cant-do-dialog";
import { STAGE_LOOK, STAGE_WORDS, stageKeyOf } from "@/lib/task-stages";
import { MadeByLine } from "@/components/made-by-line";

function fmtDateTime(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function tomorrowIso(): string {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(8, 0, 0, 0);
    return d.toISOString();
}

function nextWeekIso(): string {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(8, 0, 0, 0);
    return d.toISOString();
}

/** One-tap answers to "X wants an update" (#1842). Each posts as a comment. */
const QUICK_UPDATES = ["On it — done today", "Waiting on the customer", "Stuck — need help"];

interface UserOption {
    id: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
}

export function TaskDrawer({ taskId, onClose }: { taskId: string | null; onClose: () => void }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const { isOffice, user } = useAuth();

    const [comment, setComment] = useState("");
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [showResolution, setShowResolution] = useState(false);
    const [resolutionNote, setResolutionNote] = useState("");
    // #1842: the doer's tick of the asker's "Done looks like" line.
    const [dllMet, setDllMet] = useState(false);
    const [showSnooze, setShowSnooze] = useState(false);
    const [snoozeDate, setSnoozeDate] = useState("");
    const [mentionPick, setMentionPick] = useState("");
    // Tasks talk back (#1645)
    const [loopPanel, setLoopPanel] = useState<null | "not_handled" | "waiting" | "pass" | "reassign">(null);
    const [loopText, setLoopText] = useState("");
    const [loopDate, setLoopDate] = useState("");
    const [reassignTo, setReassignTo] = useState("");
    // Slice 3: "Can't do. Why?"
    const [showCantDo, setShowCantDo] = useState(false);
    const commentRef = useRef<HTMLTextAreaElement | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);

    const detailQ = useQuery({
        queryKey: ["task", taskId],
        queryFn: () => api.get<TaskDetailResponse>(`/api/tasks/${taskId}`),
        enabled: !!taskId,
    });

    const timelineQ = useQuery({
        queryKey: ["task", taskId, "timeline"],
        queryFn: () => api.get<{ events: TimelineEntry[] }>(`/api/tasks/${taskId}/timeline`),
        enabled: !!taskId,
    });

    // Opening the drawer is what "Seen" means on the asker's Sent tab — log it
    // however the drawer was reached (a card, a link, the toolbar tray).
    useEffect(() => {
        if (!taskId) return;
        setLoopPanel(null);
        setLoopText("");
        setLoopDate("");
        // Two-way (#1842): opening your own ask also reads what came back, so
        // the Sent row's blue dot clears once the server has the mark.
        api.post(`/api/tasks/${taskId}/opened`)
            .then(() => qc.invalidateQueries({ queryKey: ["tasks", "sent"] }))
            .catch(() => {});
    }, [taskId, qc]);

    const usersQ = useQuery({
        queryKey: ["task-assignee-options"],
        queryFn: () => api.get<{ users: UserOption[] }>("/api/task-assignee-options"),
        enabled: !!taskId && isOffice,
    });

    function refresh() {
        qc.invalidateQueries({ queryKey: ["task", taskId] });
        qc.invalidateQueries({ queryKey: ["task-inbox"] });
        qc.invalidateQueries({ queryKey: ["task-triage"] });
        qc.invalidateQueries({ queryKey: ["tasks"] });
    }

    const loopMut = useMutation({
        mutationFn: async (
            a:
                | { kind: "confirm" }
                | { kind: "not_handled"; note: string }
                | { kind: "bump" }
                | { kind: "start" }
                | { kind: "waiting"; on: string; until: string | null }
                | { kind: "pass"; reason: string }
                | { kind: "reassign"; userId: string }
                | { kind: "pass_ack" },
        ) => {
            switch (a.kind) {
                case "confirm":
                    return api.post(`/api/tasks/${taskId}/confirm`);
                case "not_handled":
                    return api.post(`/api/tasks/${taskId}/not-handled`, { note: a.note });
                case "bump":
                    return api.post(`/api/tasks/${taskId}/bump`);
                case "start":
                    return api.post(`/api/tasks/${taskId}/start`);
                case "waiting":
                    return api.patch(`/api/tasks/${taskId}`, { waitingOn: a.on, waitingUntil: a.until });
                case "pass":
                    return api.post(`/api/tasks/${taskId}/pass`, { reason: a.reason });
                case "reassign":
                    return api.patch(`/api/tasks/${taskId}`, { assigneeUserId: a.userId, assigneeQueueId: null });
                case "pass_ack":
                    return api.post(`/api/tasks/${taskId}/pass-ack`);
            }
        },
        onSuccess: (_d, a) => {
            const t = detailQ.data?.task;
            const holder = firstName(t?.assigneeName ?? t?.handedBackByName);
            const msg: Record<string, string> = {
                confirm: "Closed.",
                not_handled: `Sent back to ${holder}. It's first on their list.`,
                bump: `Asked — it's first on ${holder}'s list until they answer`,
                start: "Marked as started — the person who asked can see it",
                waiting: "Parked — it waits on them, not on you",
                pass: "Passed back — the person who asked will see why",
                reassign: "Given — the new person starts fresh",
                pass_ack: "Left in the queue",
            };
            toast.success(msg[a.kind]);
            setLoopPanel(null);
            setLoopText("");
            setLoopDate("");
            setReassignTo("");
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't save — try again"),
    });

    const reopen = async () => {
        await api.post(`/api/tasks/${taskId}/reopen`);
        refresh();
    };

    const doneMut = useMutation({
        mutationFn: (a?: { note?: string; met?: boolean }) =>
            api.post<{ task: Task; partDone?: boolean; left?: number }>(
                `/api/tasks/${taskId}/done`,
                a?.note ? { resolutionNote: a.note, ...(a.met !== undefined ? { doneLooksLikeMet: a.met } : {}) } : {},
            ),
        onSuccess: (res) => {
            setShowResolution(false);
            setResolutionNote("");
            setDllMet(false);
            refresh();
            const back = res?.task?.status === "handed_back";
            const left = res?.partDone ? (res.left ?? 0) : 0;
            const line = res?.partDone
                ? `Your part is done. ${left} ${left === 1 ? "person" : "people"} still to go.`
                : back
                  ? `Done. ${firstName(res.task.reporterName)} sees a green check.`
                  : "Task done";
            toast.success(line, {
                duration: UNDO_TOAST_MS,
                action: {
                    label: "Undo",
                    onClick: () => {
                        reopen()
                            .then(() => toast.success("Task reopened."))
                            .catch(() => toast.error("Undo failed — reopen from the task."));
                    },
                },
            });
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    const cancelMut = useMutation({
        mutationFn: (reason: string) => api.post(`/api/tasks/${taskId}/cancel`, { reason }),
        onSuccess: () => {
            setShowCancel(false);
            setCancelReason("");
            refresh();
            toast.success("Task canceled", {
                duration: UNDO_TOAST_MS,
                action: {
                    label: "Undo",
                    onClick: () => {
                        reopen()
                            .then(() => toast.success("Task reopened."))
                            .catch(() => toast.error("Undo failed — reopen from the task."));
                    },
                },
            });
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    const claimMut = useMutation({
        mutationFn: () => api.post(`/api/tasks/${taskId}/claim`),
        onSuccess: () => {
            toast.success("Claimed — it's yours");
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    const snoozeMut = useMutation({
        mutationFn: (until: string) => api.post(`/api/tasks/${taskId}/snooze`, { until }),
        onSuccess: () => {
            setShowSnooze(false);
            setSnoozeDate("");
            toast.success("Snoozed — it'll come back");
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    const reopenMut = useMutation({
        mutationFn: () => api.post(`/api/tasks/${taskId}/reopen`),
        onSuccess: () => {
            toast.success("Task reopened");
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    // #1842: one tap answers "X wants an update" — it posts as a comment, which
    // reaches the asker's Sent tab and clears the ask.
    const updateMut = useMutation({
        mutationFn: (body: string) => api.post(`/api/tasks/${taskId}/comments`, { body }),
        onSuccess: () => {
            toast.success(`Sent — ${firstName(detailQ.data?.task.reporterName)} sees it on their Sent tab`);
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't send — try again"),
    });

    const commentMut = useMutation({
        mutationFn: () => api.post(`/api/tasks/${taskId}/comments`, { body: comment.trim() }),
        onSuccess: () => {
            setComment("");
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to comment"),
    });

    const uploadMut = useMutation({
        mutationFn: (file: File) => {
            const fd = new FormData();
            fd.append("file", file);
            return api.postForm(`/api/tasks/${taskId}/attachments`, fd);
        },
        onSuccess: () => {
            toast.success("Attachment uploaded");
            refresh();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Upload failed"),
    });

    const d = detailQ.data;
    const task = d?.task;
    const quick = task ? quickActionTarget(task.quickAction, d?.links ?? []) : null;
    const chip = task && task.status === "open" ? ageChip(task.createdAt) : null;
    const users = (usersQ.data?.users ?? []).filter((u) => u.is_active && u.id !== user?.id);

    function insertMention(userId: string) {
        const u = users.find((x) => x.id === userId);
        if (!u) return;
        const token = `@[${u.first_name} ${u.last_name}](user:${userId}) `;
        setComment((c) => (c.endsWith(" ") || c.length === 0 ? c + token : `${c} ${token}`));
        setMentionPick("");
        commentRef.current?.focus();
    }

    // A task someone else gave you goes back to them with one line (#1645).
    const handsBack = !!task?.reporterUserId && task.reporterUserId !== user?.id;
    const iAmAsker = !!task && task.reporterUserId === user?.id;
    const iHoldIt = !!task && task.assigneeUserId === user?.id;
    // Shared, everyone must finish (10/16): my Done checks off my part only.
    const myPartOnly =
        !!task &&
        task.doneRule === "everyone" &&
        (d?.holders ?? []).some((h) => h.userId === user?.id) &&
        (d?.holders ?? []).some((h) => h.userId !== user?.id && h.state !== "done");
    const role = (user?.role ?? "").toLowerCase();
    const teamLead = role === "admin" || role === "manager";
    const canAskerAct = iAmAsker || teamLead;

    function handleDone() {
        if (task?.promptResolutionNote || handsBack) {
            setDllMet(false);
            setShowResolution(true);
        } else {
            doneMut.mutate(undefined);
        }
    }

    const railTone: RailTone = !task
        ? "normal"
        : task.status === "handed_back" && task.outcome === "cant_do"
          ? "bad"
          : task.status === "handed_back" || task.status === "done"
            ? "done"
            : task.passedBackAt && task.assigneeQueueId
              ? "passed"
              : task.waitingOn
                ? "waiting"
                : task.dueDate && task.status === "open" && task.dueDate < orgTodayKey()
                  ? "bad"
                  : "normal";

    return (
        <Sheet open={!!taskId} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full sm:w-[440px] sm:max-w-[440px] overflow-y-auto">
                {!task ? (
                    // Three states, not two. "Task not found." used to cover
                    // every failure, so a 500 read to the office as a deleted
                    // task and the real fault stayed invisible (#1440).
                    <div className="p-6 text-sm text-foreground/70">
                        {detailQ.isLoading ? (
                            "Loading…"
                        ) : detailQ.isError ? (
                            <div className="space-y-2">
                                <p className="text-foreground">This task wouldn't open.</p>
                                <p>
                                    It's still there — something went wrong loading it. Try again, and tell the office
                                    if it keeps failing.
                                </p>
                                <Button size="sm" variant="outline" onClick={() => detailQ.refetch()}>
                                    <RotateCcw className="h-3.5 w-3.5" /> Try again
                                </Button>
                            </div>
                        ) : (
                            "Task not found."
                        )}
                    </div>
                ) : task.whatsNewNoteId && iHoldIt ? (
                    <DrawerNote taskId={task.id} onDone={onClose} />
                ) : (
                    <>
                        <SheetHeader>
                            <div className="flex items-start gap-2 pr-8">
                                {task.priority === "urgent" && (
                                    <AlertTriangle className="h-4 w-4 mt-1 shrink-0 text-destructive" />
                                )}
                                <SheetTitle className="leading-snug">{task.displayTitle ?? task.title}</SheetTitle>
                            </div>
                            {/* Slice 5: who or what made it. */}
                            <MadeByLine madeBy={task.madeBy} />
                            {/* V2 title rewrites never destroy the original. */}
                            {task.displayTitle && task.displayTitle !== task.title && (
                                <p className="text-xs text-muted-foreground" data-testid="original-title">
                                    Original: {task.title}
                                </p>
                            )}
                            <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                {/* Slice 3: the stage in the six words, one helper everywhere. */}
                                <span
                                    className={cn(
                                        "rounded-full px-2 py-0.5 font-medium",
                                        task.status === "canceled"
                                            ? "bg-muted text-foreground/70"
                                            : STAGE_LOOK[stageKeyOf(task)].pill,
                                    )}
                                    data-testid="drawer-stage"
                                >
                                    {task.status === "canceled" ? "Canceled" : STAGE_WORDS[stageKeyOf(task)]}
                                </span>
                                {task.typeName && (
                                    <span className="rounded-full border px-2 py-0.5">{task.typeName}</span>
                                )}
                                {task.priority === "urgent" && (
                                    <span className="rounded-full bg-destructive/10 px-2 py-0.5 font-semibold text-destructive">
                                        Urgent
                                    </span>
                                )}
                                {chip && (
                                    <span
                                        className={cn(
                                            "rounded-full px-2 py-0.5 font-medium",
                                            chip.amber ? "bg-amber-500/15 text-amber-600" : "bg-muted",
                                        )}
                                    >
                                        {chip.label}
                                    </span>
                                )}
                                {task.snoozedUntil && new Date(task.snoozedUntil) > new Date() && (
                                    <span className="rounded-full bg-muted px-2 py-0.5">
                                        Snoozed until {fmtDateTime(task.snoozedUntil)}
                                    </span>
                                )}
                            </div>
                        </SheetHeader>

                        <div className="flex-1 space-y-4 p-4">
                            {/* Tasks talk back (#1645): where it stands, at a glance. */}
                            {task.status !== "canceled" && (
                                <TaskProgressRail stage={task.stage} tone={railTone} showLabels />
                            )}

                            {task.bumped &&
                                task.status === "open" &&
                                iHoldIt &&
                                (() => {
                                    const w = bumpWords(task, firstName(task.bumpedByName ?? task.reporterName));
                                    return (
                                        <div
                                            className={cn(
                                                "space-y-2 rounded-lg px-3 py-2 text-sm",
                                                w.tone === "due"
                                                    ? "bg-red-100 text-red-950 dark:bg-red-950 dark:text-red-100"
                                                    : "bg-amber-100 text-amber-950 dark:bg-amber-950 dark:text-amber-100",
                                            )}
                                            data-testid="drawer-bump"
                                        >
                                            <div className="flex items-center gap-2">
                                                {w.tone === "due" ? (
                                                    <AlarmClock className="h-4 w-4 shrink-0" />
                                                ) : (
                                                    <Hand className="h-4 w-4 shrink-0" />
                                                )}
                                                <span>
                                                    <strong className="font-semibold">{w.head}</strong>
                                                    {w.tail ? ` ${w.tail}` : ""}
                                                </span>
                                            </div>
                                            {(task.bumpReason ?? (task.autoBumped ? "unopened" : "asked")) ===
                                                "asked" && (
                                                <div className="flex flex-wrap gap-1.5" data-testid="quick-updates">
                                                    {QUICK_UPDATES.map((q) => (
                                                        <Button
                                                            key={q}
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 rounded-full bg-background text-xs"
                                                            disabled={updateMut.isPending}
                                                            onClick={() => updateMut.mutate(q)}
                                                        >
                                                            {q}
                                                        </Button>
                                                    ))}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 text-xs"
                                                        onClick={() => commentRef.current?.focus()}
                                                    >
                                                        Write my own
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                            {/* Who / when */}
                            <div className="text-xs text-foreground/70 space-y-0.5">
                                <div>
                                    {task.reporterName ? `${firstName(task.reporterName)} asked ` : "Assigned to "}
                                    <span className="font-medium text-foreground">
                                        {task.assigneeUserId === user?.id
                                            ? "you"
                                            : (task.assigneeName ??
                                              (task.queueName ? `the ${task.queueName} queue` : "—"))}
                                    </span>
                                    {task.status === "open" &&
                                        task.reporterName &&
                                        ` · waiting ${waitedFor(task.createdAt)}`}
                                </div>
                                <div>
                                    Created {fmtDateTime(task.createdAt)}
                                    {task.dueDate ? ` · due ${task.dueDate}` : ""}
                                    {task.doneAt ? ` · closed ${fmtDateTime(task.doneAt)}` : ""}
                                </div>
                            </div>

                            {/* Details */}
                            {task.detailsRich && (
                                <div className="rounded-lg border bg-muted/40 p-3 text-sm whitespace-pre-wrap">
                                    {task.detailsRich}
                                </div>
                            )}

                            {task.doneLooksLike && (
                                <div className="rounded-lg bg-secondary px-3 py-2 text-sm">
                                    <span className="font-semibold">Done looks like: </span>
                                    {task.doneLooksLike}
                                </div>
                            )}

                            {task.resolutionNote && task.status !== "handed_back" && (
                                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
                                    <span className="font-medium">Resolution: </span>
                                    {task.resolutionNote}
                                </div>
                            )}
                            {task.cancelReason && (
                                <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                                    <span className="font-medium">Cancel reason: </span>
                                    {task.cancelReason}
                                </div>
                            )}

                            {/* Book My Test: Approve / Decline a "Card needed" or "Terms requested" request. */}
                            {taskId && <BookingApprovalPanel taskId={taskId} onDecided={refresh} />}

                            {/* Links */}
                            {(d?.links.length ?? 0) > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {d!.links.map((l) => (
                                        <Link
                                            key={`${l.entityType}:${l.entityId}`}
                                            to={l.href}
                                            onClick={onClose}
                                            className="inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-1 text-xs hover:bg-muted transition-colors"
                                        >
                                            <Link2 className="h-3 w-3" />
                                            {l.label}
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {/* Quick action */}
                            {quick && task.status === "open" && (
                                <Button
                                    size="sm"
                                    className="w-full"
                                    onClick={() => {
                                        onClose();
                                        navigate(quick.href);
                                    }}
                                >
                                    {quick.label} <ArrowRight className="h-3.5 w-3.5" />
                                </Button>
                            )}

                            {/* ── Slice 3: Can't do, back in red with the reason ── */}
                            {task.status === "handed_back" && task.outcome === "cant_do" && (
                                <CantDoPanel task={task} canDecide={canAskerAct} people={users} onChanged={refresh} />
                            )}

                            {/* ── The asker's side (#1645) ── */}
                            {task.status === "handed_back" && task.outcome !== "cant_do" && (
                                <div className="space-y-3 rounded-xl border border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950/40">
                                    <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                                        <Check className="h-4 w-4" /> {firstName(task.handedBackByName)} did it
                                        {task.handedBackAt ? ` · ${fmtDateTime(task.handedBackAt)}` : ""}
                                    </p>
                                    {task.resolutionNote && (
                                        <p className="text-sm leading-relaxed text-foreground">
                                            “{task.resolutionNote}”
                                        </p>
                                    )}
                                    {task.doneLooksLike && typeof task.doneLooksLikeMet === "boolean" && (
                                        <DoneLooksLikeResult
                                            line={task.doneLooksLike}
                                            met={task.doneLooksLikeMet}
                                            who={firstName(task.handedBackByName)}
                                        />
                                    )}
                                    {canAskerAct ? (
                                        loopPanel === "not_handled" ? (
                                            <div className="space-y-2">
                                                <Textarea
                                                    rows={2}
                                                    value={loopText}
                                                    onChange={(e) => setLoopText(e.target.value)}
                                                    placeholder={`What's still missing? ${firstName(task.handedBackByName)} sees this first.`}
                                                    autoFocus
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        className="bg-amber-800 text-white hover:bg-amber-900"
                                                        disabled={!loopText.trim() || loopMut.isPending}
                                                        onClick={() =>
                                                            loopMut.mutate({
                                                                kind: "not_handled",
                                                                note: loopText.trim(),
                                                            })
                                                        }
                                                    >
                                                        Send back to {firstName(task.handedBackByName)}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setLoopPanel(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {/* Slice 4 (10/28, pick 1A): nothing to press. */}
                                                <p className="text-xs text-foreground/80">
                                                    Nothing to press. It closes by itself after 2 working days.
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => setLoopPanel("not_handled")}
                                                        data-testid="drawer-not-right"
                                                    >
                                                        Not right
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        className="text-emerald-800 dark:text-emerald-300"
                                                        disabled={loopMut.isPending}
                                                        onClick={() => loopMut.mutate({ kind: "confirm" })}
                                                    >
                                                        <Check className="h-4 w-4" /> Close it now
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        <p className="text-xs text-foreground/80">
                                            {firstName(task.reporterName)} sees a green check. It closes by itself after
                                            2 working days unless they say it&apos;s not right.
                                        </p>
                                    )}
                                </div>
                            )}

                            {task.status === "open" && task.passedBackAt && task.assigneeQueueId && canAskerAct && (
                                <div className="space-y-2 rounded-xl border border-orange-300 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950/40">
                                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                                        {firstName(task.passedBackByName)} passed this back
                                    </p>
                                    <p className="text-sm text-foreground">
                                        {task.passReason ? `“${task.passReason}”` : "No reason given."} It&apos;s in the{" "}
                                        {task.queueName} queue and nobody has taken it.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => setLoopPanel(loopPanel === "reassign" ? null : "reassign")}
                                        >
                                            Give to someone
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={loopMut.isPending || !user}
                                            onClick={() =>
                                                user && loopMut.mutate({ kind: "reassign", userId: user.id })
                                            }
                                        >
                                            I&apos;ll do it
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            disabled={loopMut.isPending}
                                            onClick={() => loopMut.mutate({ kind: "pass_ack" })}
                                        >
                                            Leave in queue
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {task.status === "open" && canAskerAct && !iHoldIt && !task.passedBackAt && (
                                <div
                                    className="space-y-2 rounded-xl border bg-secondary/40 p-3"
                                    data-testid="asker-actions"
                                >
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/80">
                                        You asked for this
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {task.assigneeUserId && (
                                            <Button
                                                size="sm"
                                                disabled={loopMut.isPending || (task.bumped && !task.autoBumped)}
                                                onClick={() => loopMut.mutate({ kind: "bump" })}
                                            >
                                                <Hand className="h-3.5 w-3.5" />
                                                {task.bumped && !task.autoBumped
                                                    ? "Asked ✓ · waiting on their answer"
                                                    : "Ask for an update"}
                                            </Button>
                                        )}
                                        {isOffice && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    setLoopPanel(loopPanel === "reassign" ? null : "reassign")
                                                }
                                            >
                                                Give to someone else
                                            </Button>
                                        )}
                                    </div>
                                    {task.assigneeUserId && (
                                        <p className="text-xs text-muted-foreground">
                                            It goes first on {firstName(task.assigneeName)}&apos;s list with one-tap
                                            answers. Their answer shows here and as a mark on your Sent tab. Never a
                                            text or a phone ping.
                                        </p>
                                    )}
                                </div>
                            )}

                            {loopPanel === "reassign" && task.status === "open" && (
                                <div className="flex flex-wrap items-center gap-2 rounded-lg border p-3">
                                    <Select
                                        value={reassignTo}
                                        onChange={(e) => setReassignTo(e.target.value)}
                                        className="h-9 min-w-[180px] flex-1 text-sm"
                                        aria-label="Give it to"
                                    >
                                        <option value="">Give it to…</option>
                                        {users
                                            .filter((u) => u.id !== task.assigneeUserId)
                                            .map((u) => (
                                                <option key={u.id} value={u.id}>
                                                    {u.first_name} {u.last_name}
                                                </option>
                                            ))}
                                    </Select>
                                    <Button
                                        size="sm"
                                        disabled={!reassignTo || loopMut.isPending}
                                        onClick={() => loopMut.mutate({ kind: "reassign", userId: reassignTo })}
                                    >
                                        Give it
                                    </Button>
                                </div>
                            )}

                            {/* Who is on it: Take it / Help / shared tasks (10/16) */}
                            <TaskPeople
                                task={task}
                                holders={d?.holders ?? []}
                                me={user?.id}
                                isOffice={isOffice}
                                people={users}
                                onChanged={refresh}
                            />

                            {/* State actions */}
                            {task.status === "open" ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {task.assigneeQueueId && isOffice && (
                                        <Button size="sm" variant="outline" onClick={() => claimMut.mutate()}>
                                            <Hand className="h-3.5 w-3.5" /> Claim
                                        </Button>
                                    )}
                                    {iHoldIt && !task.startedAt && !task.waitingOn && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={loopMut.isPending}
                                            onClick={() => loopMut.mutate({ kind: "start" })}
                                        >
                                            <Play className="h-3.5 w-3.5" /> I&apos;m on it
                                        </Button>
                                    )}
                                    <Button size="sm" onClick={handleDone} disabled={doneMut.isPending}>
                                        <Check className="h-3.5 w-3.5" />{" "}
                                        {myPartOnly
                                            ? "My part is done"
                                            : handsBack
                                              ? `Hand back to ${firstName(task.reporterName)}`
                                              : "Done"}
                                    </Button>
                                    {iHoldIt && isOffice && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setLoopPanel(loopPanel === "waiting" ? null : "waiting")}
                                        >
                                            <Hourglass className="h-3.5 w-3.5" /> Waiting on…
                                        </Button>
                                    )}
                                    {iHoldIt && isOffice && handsBack && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setLoopPanel(loopPanel === "pass" ? null : "pass")}
                                        >
                                            <Undo2 className="h-3.5 w-3.5" /> Pass back
                                        </Button>
                                    )}
                                    {iHoldIt && handsBack && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-red-300 text-red-800 hover:bg-red-50 dark:border-red-800 dark:text-red-300"
                                            onClick={() => setShowCantDo(true)}
                                            data-testid="drawer-cant-do"
                                        >
                                            <Ban className="h-3.5 w-3.5" /> Can&apos;t do…
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => setShowSnooze((s) => !s)}>
                                        <CalendarClock className="h-3.5 w-3.5" /> Snooze
                                    </Button>
                                    {isOffice && (
                                        <Button size="sm" variant="outline" onClick={() => setShowCancel((s) => !s)}>
                                            <X className="h-3.5 w-3.5" /> Cancel
                                        </Button>
                                    )}
                                </div>
                            ) : task.status === "handed_back" && !canAskerAct ? (
                                <Button size="sm" variant="outline" onClick={() => reopenMut.mutate()}>
                                    <RotateCcw className="h-3.5 w-3.5" /> Take it back — not finished
                                </Button>
                            ) : task.status !== "handed_back" ? (
                                <Button size="sm" variant="outline" onClick={() => reopenMut.mutate()}>
                                    <RotateCcw className="h-3.5 w-3.5" /> Reopen
                                </Button>
                            ) : null}

                            {loopPanel === "waiting" && task.status === "open" && (
                                <div className="space-y-2 rounded-lg border p-3">
                                    <p className="text-xs font-medium">
                                        Who has the ball? It comes back to you on the date.
                                    </p>
                                    <Input
                                        value={loopText}
                                        onChange={(e) => setLoopText(e.target.value)}
                                        placeholder="The customer, the city, Reece Plumbing…"
                                        maxLength={160}
                                    />
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Input
                                            type="date"
                                            value={loopDate}
                                            onChange={(e) => setLoopDate(e.target.value)}
                                            className="h-9 w-40"
                                            aria-label="Check back on"
                                        />
                                        <Button
                                            size="sm"
                                            disabled={!loopText.trim() || loopMut.isPending}
                                            onClick={() =>
                                                loopMut.mutate({
                                                    kind: "waiting",
                                                    on: loopText.trim(),
                                                    until: loopDate || null,
                                                })
                                            }
                                        >
                                            Park it
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {loopPanel === "pass" && task.status === "open" && (
                                <div className="space-y-2 rounded-lg border p-3">
                                    <p className="text-xs font-medium">
                                        Why isn&apos;t it yours? {firstName(task.reporterName)} sees this.
                                    </p>
                                    <Input
                                        value={loopText}
                                        onChange={(e) => setLoopText(e.target.value)}
                                        placeholder="One line"
                                        maxLength={1000}
                                    />
                                    <Button
                                        size="sm"
                                        disabled={!loopText.trim() || loopMut.isPending}
                                        onClick={() => loopMut.mutate({ kind: "pass", reason: loopText.trim() })}
                                    >
                                        Pass back to {firstName(task.reporterName)}
                                    </Button>
                                </div>
                            )}

                            {showSnooze && task.status === "open" && (
                                <div className="rounded-lg border p-3 space-y-2">
                                    <p className="text-xs font-medium">Snooze until</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => snoozeMut.mutate(tomorrowIso())}
                                        >
                                            Tomorrow
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => snoozeMut.mutate(nextWeekIso())}
                                        >
                                            Next week
                                        </Button>
                                        <Input
                                            type="date"
                                            value={snoozeDate}
                                            onChange={(e) => setSnoozeDate(e.target.value)}
                                            className="h-8 w-36"
                                        />
                                        <Button
                                            size="sm"
                                            disabled={!snoozeDate}
                                            onClick={() =>
                                                snoozeMut.mutate(new Date(`${snoozeDate}T08:00:00`).toISOString())
                                            }
                                        >
                                            Snooze
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {showCancel && task.status === "open" && (
                                <div className="rounded-lg border p-3 space-y-2">
                                    <p className="text-xs font-medium">Why is this no longer needed?</p>
                                    <Input
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        placeholder="One-line reason (required)"
                                        maxLength={1000}
                                    />
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        disabled={!cancelReason.trim() || cancelMut.isPending}
                                        onClick={() => cancelMut.mutate(cancelReason.trim())}
                                    >
                                        Cancel task
                                    </Button>
                                </div>
                            )}

                            {showResolution && task.status === "open" && (
                                <div className="rounded-lg border p-3 space-y-2">
                                    <p className="text-xs font-medium">
                                        {handsBack
                                            ? `What happened? ${firstName(task.reporterName)} gets this right away and confirms it.`
                                            : "How was this resolved?"}
                                    </p>
                                    <Textarea
                                        value={resolutionNote}
                                        onChange={(e) => setResolutionNote(e.target.value)}
                                        placeholder={
                                            handsBack ? "One line is plenty (required)" : "Short resolution note"
                                        }
                                        rows={2}
                                        autoFocus
                                    />
                                    {handsBack && task.doneLooksLike && (
                                        <label className="flex cursor-pointer items-start gap-2 rounded-lg bg-secondary px-3 py-2 text-sm">
                                            <input
                                                type="checkbox"
                                                className="mt-0.5 h-4 w-4"
                                                checked={dllMet}
                                                onChange={(e) => setDllMet(e.target.checked)}
                                                data-testid="drawer-dll-tick"
                                            />
                                            <span>
                                                <span className="font-semibold">Done looks like: </span>
                                                {task.doneLooksLike}
                                            </span>
                                        </label>
                                    )}
                                    <div className="flex gap-1.5">
                                        <Button
                                            size="sm"
                                            disabled={doneMut.isPending || (handsBack && !resolutionNote.trim())}
                                            onClick={() =>
                                                doneMut.mutate({
                                                    note: resolutionNote.trim() || undefined,
                                                    met: handsBack && task.doneLooksLike ? dllMet : undefined,
                                                })
                                            }
                                        >
                                            {handsBack ? `Hand back to ${firstName(task.reporterName)}` : "Mark done"}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setShowResolution(false)}>
                                            Back
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* What happened (#1645) */}
                            {(timelineQ.data?.events.length ?? 0) > 0 && (
                                <div className="space-y-2" data-testid="task-timeline">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                                        What happened
                                    </p>
                                    <ol className="space-y-0">
                                        {timelineQ.data!.events.map((e, idx) => (
                                            <li
                                                key={`${e.at}-${idx}`}
                                                className="grid grid-cols-[14px_minmax(0,1fr)] gap-2.5"
                                            >
                                                <span className="flex flex-col items-center">
                                                    <span
                                                        className={cn(
                                                            "mt-1 h-2.5 w-2.5 shrink-0 rounded-full",
                                                            e.tone === "good"
                                                                ? "bg-emerald-700"
                                                                : e.tone === "bad"
                                                                  ? "bg-red-700"
                                                                  : e.tone === "warn"
                                                                    ? "bg-amber-600"
                                                                    : e.tone === "wait"
                                                                      ? "bg-violet-700"
                                                                      : "bg-stone-400",
                                                        )}
                                                    />
                                                    {idx < timelineQ.data!.events.length - 1 && (
                                                        <span className="w-0.5 flex-1 bg-border" />
                                                    )}
                                                </span>
                                                <span className="pb-3">
                                                    <span className="block text-sm leading-snug">{e.text}</span>
                                                    <span className="block text-xs text-muted-foreground">
                                                        {fmtDateTime(e.at)}
                                                    </span>
                                                </span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}

                            {/* Attachments */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                                        Attachments
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => fileRef.current?.click()}
                                        disabled={uploadMut.isPending}
                                    >
                                        <Paperclip className="h-3.5 w-3.5" />
                                        {uploadMut.isPending ? "Uploading…" : "Add"}
                                    </Button>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) uploadMut.mutate(f);
                                            e.target.value = "";
                                        }}
                                    />
                                </div>
                                {(d?.attachments.length ?? 0) === 0 ? (
                                    <p className="text-xs text-foreground/60">No attachments</p>
                                ) : (
                                    <ul className="space-y-1">
                                        {d!.attachments.map((a) => (
                                            <li key={a.id}>
                                                <button
                                                    className="inline-flex max-w-full items-center gap-1.5 text-xs text-primary hover:underline"
                                                    onClick={() =>
                                                        downloadAuthedFile(
                                                            `/api/tasks/${taskId}/attachments/${a.id}/download`,
                                                            a.fileName,
                                                        ).catch(() => toast.error("Download failed"))
                                                    }
                                                >
                                                    <Download className="h-3 w-3 shrink-0" />
                                                    <span className="truncate">{a.fileName}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Comments */}
                            <div className="space-y-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                                    Comments
                                </p>
                                {(d?.comments.length ?? 0) === 0 ? (
                                    <p className="text-xs text-foreground/60">No comments yet</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {d!.comments.map((c) => (
                                            <li key={c.id} className="rounded-lg border bg-muted/30 p-2.5">
                                                <div className="flex items-center justify-between text-[11px] text-foreground/60">
                                                    <span className="font-medium text-foreground/90">
                                                        {c.authorName ?? "Unknown"}
                                                    </span>
                                                    <span>{fmtDateTime(c.createdAt)}</span>
                                                </div>
                                                <p className="mt-1 text-sm whitespace-pre-wrap">
                                                    {renderMentionText(c.body)}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="space-y-1.5">
                                    <Textarea
                                        ref={commentRef}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="Add a comment…"
                                        rows={2}
                                    />
                                    <div className="flex items-center gap-1.5">
                                        {isOffice && users.length > 0 && (
                                            <Select
                                                value={mentionPick}
                                                onChange={(e) => insertMention(e.target.value)}
                                                className="h-8 w-36 text-xs"
                                            >
                                                <option value="">@ Mention…</option>
                                                {users.map((u) => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.first_name} {u.last_name}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                        <Button
                                            size="sm"
                                            className="ml-auto"
                                            disabled={!comment.trim() || commentMut.isPending}
                                            onClick={() => commentMut.mutate()}
                                        >
                                            Comment
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </SheetContent>
            {task && (
                <CantDoDialog
                    open={showCantDo}
                    taskId={task.id}
                    taskTitle={task.displayTitle ?? task.title}
                    askerName={task.reporterName}
                    onDone={refresh}
                    onClose={() => setShowCantDo(false)}
                />
            )}
        </Sheet>
    );
}

/**
 * Slice 3: a can't-do, back with the person who asked — red, with the
 * reason, and three answers: I'll do it, Give to…, Drop it.
 */
function CantDoPanel({
    task,
    canDecide,
    people,
    onChanged,
}: {
    task: Task;
    canDecide: boolean;
    people: UserOption[];
    onChanged: () => void;
}) {
    const decide = useCantDoDecide(task.id, onChanged);
    const [giving, setGiving] = useState(false);
    const [to, setTo] = useState("");
    const who = firstName(task.handedBackByName);
    return (
        <div
            className="space-y-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40"
            data-testid="cant-do-panel"
        >
            <p className="flex items-center gap-1.5 text-sm font-bold text-red-900 dark:text-red-200">
                <Ban className="h-4 w-4" /> {who} can&apos;t do this
                {task.handedBackAt ? ` · ${fmtDateTime(task.handedBackAt)}` : ""}
            </p>
            {task.cantDoReason && <p className="text-sm leading-relaxed text-foreground">“{task.cantDoReason}”</p>}
            {canDecide ? (
                giving ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={to} onChange={(e) => setTo(e.target.value)} className="h-9 min-w-[180px] flex-1">
                            <option value="">Pick a person…</option>
                            {people.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name}
                                </option>
                            ))}
                        </Select>
                        <Button
                            size="sm"
                            disabled={!to || decide.isPending}
                            onClick={() => decide.mutate({ decision: "give", userId: to })}
                        >
                            Give it
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setGiving(false)}>
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ decision: "mine" })}
                            data-testid="cant-do-mine"
                        >
                            I&apos;ll do it
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setGiving(true)}>
                            Give to…
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ decision: "drop" })}
                            data-testid="cant-do-drop"
                        >
                            Drop it
                        </Button>
                    </div>
                )
            ) : (
                <p className="text-xs text-foreground/80">
                    It went back to {firstName(task.reporterName)} to decide what happens next.
                </p>
            )}
        </div>
    );
}
